import qrcode from "qrcode-generator";
import { BookingPageDraftResponse } from "../contracts/BookingPageDraftResponse";
import { BookingPageServiceContract } from "../contracts/BookingPageServiceContract";
import { BookingPageSettingsResponse } from "../contracts/BookingPageSettingsResponse";
import { UpdateBookingPageSettingsRequest } from "../contracts/UpdateBookingPageSettingsRequest";

/**
 * The shareable booking-page URL — the instructor's own subdomain. The
 * marketing server rewrites it to the visitor's language internally, so one
 * link works for everyone (canonical stays on timeplanned.com/book/...).
 */
export function bookingPageUrl(slug: string): string {
  return `https://${slug}.timeplanned.com`;
}

/** The shareable standalone contact-form URL on the instructor's subdomain. */
export function bookingContactUrl(slug: string): string {
  return `https://${slug}.timeplanned.com/contact`;
}

/** The shareable link-in-bio page URL on the instructor's subdomain. */
export function bookingLinksUrl(slug: string): string {
  return `https://${slug}.timeplanned.com/links`;
}

/**
 * The booking-page URL as a print-quality QR code (SVG string). Medium error
 * correction survives flyer wear; the quiet zone (margin) keeps scanners happy.
 */
export function buildBookingPageQrSvg(url: string): string {
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  return qr.createSvgTag({ cellSize: 8, margin: 32, scalable: true });
}

/** SVG string → data URL usable in <img> and canvas. */
export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * AI-improved services map 1:1 onto the existing rows (same order), so prices,
 * icons and pictures survive. On any count mismatch the existing services win.
 */
export function mergeDraftServices(
  settings: BookingPageSettingsResponse,
  draft: BookingPageDraftResponse,
  mode: "generate" | "improve",
): BookingPageServiceContract[] {
  if (settings.services.length === 0) {
    return draft.services.map((s) => ({ title: s.title, description: s.description }));
  }
  if (mode === "improve" && draft.services.length === settings.services.length) {
    return settings.services.map((existing, i) => ({
      ...existing,
      title: draft.services[i].title,
      description: draft.services[i].description,
    }));
  }
  return settings.services;
}

/**
 * Full round-trip of the current settings into an update request, so a card
 * saving its own fields never resets what the other cards manage. Spread it
 * and override just the fields you edit.
 */
export function toUpdateRequest(
  settings: BookingPageSettingsResponse,
): UpdateBookingPageSettingsRequest {
  return {
    enabled: settings.enabled,
    slug: settings.slug,
    accentColor: settings.accentColor,
    theme: settings.theme,
    displayName: settings.displayName,
    introText: settings.introText,
    contactIntroText: settings.contactIntroText,
    layout: settings.layout,
    headline: settings.headline,
    aboutText: settings.aboutText,
    locationText: settings.locationText,
    showOpeningHours: settings.showOpeningHours,
    services: settings.services,
    links: settings.links,
    ctaText: settings.ctaText,
  };
}
